/* eslint-disable max-classes-per-file */
import { Injectable, OnDestroy, inject } from "@angular/core";
import { Subject } from "rxjs";
import { debounceTime, filter, takeUntil } from "rxjs/operators";
import { ArbetsorderViol3SynchronizationData } from "~app/common/events/saga/ArbetsorderViol3SynchronizationData";
import { SagaStatusEventMessage } from "~app/common/events/saga/SagaStatusEventMessage";
import { SagaStatusEventMessageArbetsorderData } from "~app/common/events/saga/SagaStatusEventMessageArbetsorderData";
import { ArbetsorderWorkTypeConstants, Viol3Status } from "~app/common/models/constants";
import { ApiClientService } from "~app/common/services/api-client.service";
import { EventService } from "~app/common/services/cqs/event.service";
import { EventHubService } from "~app/common/services/eventhub.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ArbetsorderWorkEventMessage } from "~app/features/planera/events/ArbetsorderWorkEventMessage";
import { DestineringsalternativMergeCompletedEventMessage } from "~app/features/planera/events/DestineringsalternativMergeCompletedEventMessage";
import { ArbetsorderService, GetArbetsorderAlertsQueryResult } from "~app/features/planera/services/arbetsorder.service";
@Injectable({
    providedIn: "root"
})
export class ArbetsorderEventStore implements OnDestroy {

    arbetsorderStoreEventsLoaded$ = new Subject<Map<number, ArbetsorderEventStoreInfo>>();

    arbetsorderSynchFinished$ = new Subject<ArbetsorderEventStoreInfo>();
    arbetsorderSynchAbort$ = new Subject<ArbetsorderEventStoreInfo>();
    arbetsorderSendStatusChanged$ = new Subject<ArbetsorderEventStoreInfo>();
    destineringsalternativMergeCompleted$ = new Subject<ArbetsorderEventStoreInfo>();
    arbetsorderDestineringsalternativWorkCompleted$ = new Subject<ArbetsorderEventStoreInfo>();
    alertFetchedCompleted$ = new Subject<ArbetsorderEventStoreInfo>();

    private arbetsorderMap: Map<number, ArbetsorderEventStoreInfo> = new Map<number, ArbetsorderEventStoreInfo>();
    private readonly arbetsorderCounter: Map<number, number> = new Map<number, number>();
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly arbetsorderQueue = new Set<number>();
    private readonly debounceSubject = new Subject<void>();

    private readonly eventHubService = inject(EventHubService);
    private readonly arbetsorderService = inject(ArbetsorderService);
    private readonly apiClientService = inject(ApiClientService);
    private readonly eventService = inject(EventService);

    constructor() {
        this.debounceSubject.pipe(
            debounceTime(300)
        ).subscribe(() => this.processArbetsorderQueue());
        this.setUpEvents();
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.debounceSubject.complete();
    }

    addArbetsorderList(arbetsorderIds: number[]) {
        arbetsorderIds.forEach(arbetsorderId => {
            if (!this.arbetsorderCounter.has(arbetsorderId)) {
                this.arbetsorderCounter.set(arbetsorderId, 1);
                this.eventHubService.registerListener(`arbetsorder/${arbetsorderId.toString()}`);
            } else {
                this.arbetsorderCounter.set(arbetsorderId, (this.arbetsorderCounter.get(arbetsorderId) ?? 0) + 1);
            }
            this.arbetsorderQueue.add(arbetsorderId);
        });
        this.debounceSubject.next();
    }

    removeArbetsorderList(arbetsorderIds: number[]) {
        arbetsorderIds.forEach(arbetsorderId => {
            if (this.arbetsorderCounter.has(arbetsorderId)) {
                let count = this.arbetsorderCounter.get(arbetsorderId) ?? 0;
                count--;

                if (count <= 0) {
                    this.arbetsorderCounter.delete(arbetsorderId);
                    this.eventHubService.unregisterListener(`arbetsorder/${arbetsorderId.toString()}`);
                    this.arbetsorderQueue.add(arbetsorderId);
                } else {
                    this.arbetsorderCounter.set(arbetsorderId, count);
                    // Här behövs inte arbetsorderQueue eftersom vi endast avslutat en registrering
                }
            }
        });
        this.debounceSubject.next();
    }

    getAlerts(arbetsorderId: number) {
        this.arbetsorderService.getAlerts(arbetsorderId).subscribe(alertsResult => {
            const arbetsorderInfo = this.arbetsorderMap.get(arbetsorderId);
            if (arbetsorderInfo) {
                arbetsorderInfo.alertInfo = alertsResult;
                this.alertFetchedCompleted$.next(arbetsorderInfo);
            }
        });
    }
    private processArbetsorderQueue() {
        const newEntries = new Set<number>();
        const cachedEntries = new Set<number>();
        for (const id of this.arbetsorderQueue) {
            if (this.arbetsorderCounter.has(id)) {
                if (this.arbetsorderMap.has(id)) {
                    // Finns den redan i cachen så ska vi vara i synk och kan returnera cachead data
                    cachedEntries.add(id);
                } else {
                    // Finns ej i cache - måste hämtas
                    newEntries.add(id);
                }
            } else {
                // Ingen som längre lyssnar - ta bort
                this.arbetsorderMap.delete(id);
            }
        }

        if (newEntries.size > 0) {
            const params = { arbetsorderIdList: Array.from(newEntries) };
            this.apiClientService.post<any, ArbetsorderEventStoreInfo[]>(`arbetsorder/EventStoreInfo`, params, false)
                .subscribe(response => {
                    this.updateArbetsorderList(response);
                    // Emitta alla events som vi fått svar på
                    this.arbetsorderStoreEventsLoaded$.next(new Map(response.map(data => [
                        data.arbetsorderId,
                        data
                    ])));
                });
        }

        // Re-emitta events som redan finns i cache
        const eventsEmitted = new Map<number, ArbetsorderEventStoreInfo>();
        cachedEntries.forEach(x => eventsEmitted.set(x, this.arbetsorderMap.get(x)));
        this.arbetsorderStoreEventsLoaded$.next(eventsEmitted);

        this.arbetsorderQueue.clear();
    }

    private updateArbetsorderList(response: ArbetsorderEventStoreInfo[]) {
        response.forEach(x => {
            const ao = this.arbetsorderMap.get(x.arbetsorderId);
            if (!ao) {
                this.arbetsorderMap.set(x.arbetsorderId, x);
            } else {
                this.arbetsorderMap[x.arbetsorderId] = x;
            }
        });
    }

    private setUpEvents() {

        this.eventService.subscribeSagaType<SagaStatusEventMessage<SagaStatusEventMessageArbetsorderData>>(SagaStatusEventMessageArbetsorderData.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(filter(event => this.arbetsorderCounter.has(event.data.arbetsorderId)))
            .subscribe(e => {
                const arbetsorderInfo = this.arbetsorderMap.get(e.data.arbetsorderId);

                if (arbetsorderInfo && arbetsorderInfo.arbetsorderIsSending !== e.active) {
                    arbetsorderInfo.arbetsorderIsSending = e.active;
                    this.arbetsorderSendStatusChanged$.next(arbetsorderInfo);
                }
            });

        // syncronisering avslutad viol3
        this.eventService.subscribeSagaType<SagaStatusEventMessage<ArbetsorderViol3SynchronizationData>>(ArbetsorderViol3SynchronizationData.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(filter(event => this.arbetsorderCounter.has(event.data.arbetsorderId) && !event.active))
            .subscribe(e => {
                const isDirty = e.data.dirtyProduktion || e.data.dirtyTransport;
                const statusProdktion = this.getViolStatus(e.data.hasTransmissionErrorProduktion, e.data.dirtyProduktion);
                const statusTransport = this.getViolStatus(e.data.hasTransmissionErrorTransport, e.data.dirtyTransport);

                const arbetsorderInfo = this.arbetsorderMap.get(e.data.arbetsorderId);
                if (ObjectUtils.hasValue(arbetsorderInfo)) {
                    arbetsorderInfo.arbetsorderIsDirty = isDirty;
                    arbetsorderInfo.statusProduktion = statusProdktion;
                    arbetsorderInfo.statusTransport = statusTransport;
                    arbetsorderInfo.produktionsdelIsDirty = e.data.dirtyProduktion;
                    arbetsorderInfo.transportdelIsDirty = e.data.dirtyTransport;
                    arbetsorderInfo.senastSkickat = e.data.skickadTillViol3;
                    arbetsorderInfo.hasTransmissionErrorViol3 = e.data.hasTransmissionErrorProduktion || e.data.hasTransmissionErrorTransport;
                }

                this.arbetsorderSynchFinished$.next(arbetsorderInfo);
                this.getAlerts(e.data.arbetsorderId);
            });

        // syncronisering avslutad viol2
        this.eventService.subscribeEvent<ArbetsorderWorkEventMessage>(ArbetsorderWorkEventMessage.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(filter(x => this.arbetsorderCounter.has(x.arbetsorderId) &&
                x.work === ArbetsorderWorkTypeConstants.arbetsorderWorkStarted &&
                x.status === ArbetsorderWorkEventMessage.completed))
            .subscribe((event) => {
                const arbetsorderInfo = this.arbetsorderMap.get(event.arbetsorderId);
                if (arbetsorderInfo) {
                    this.arbetsorderSynchFinished$.next(arbetsorderInfo);
                }
            });

        this.eventService.subscribeEvent<ArbetsorderWorkEventMessage>(ArbetsorderWorkEventMessage.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(filter(x => this.arbetsorderCounter.has(x.arbetsorderId) &&
                x.work === ArbetsorderWorkTypeConstants.arbetsorderWorkRequested &&
                x.status === ArbetsorderWorkEventMessage.abort))
            .subscribe((event) => {
                const arbetsorderInfo = this.arbetsorderMap.get(event.arbetsorderId);
                if (arbetsorderInfo) {
                    this.arbetsorderSynchAbort$.next(arbetsorderInfo);
                }
            });

        this.eventService.subscribeEvent<DestineringsalternativMergeCompletedEventMessage>(DestineringsalternativMergeCompletedEventMessage.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(filter(x => this.arbetsorderCounter.has(x.arbetsorderId)))
            .subscribe(event => {
                const arbetsorderInfo = this.arbetsorderMap.get(event.arbetsorderId);
                if (arbetsorderInfo) {
                    this.destineringsalternativMergeCompleted$.next(arbetsorderInfo);
                }
            });

        this.eventService.subscribeEvent<ArbetsorderWorkEventMessage>(ArbetsorderWorkEventMessage.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .pipe(filter(x => this.arbetsorderCounter.has(x.arbetsorderId) &&
                x.work === ArbetsorderWorkTypeConstants.destineringsalternativWorkArbetsorder &&
                x.status === ArbetsorderWorkEventMessage.completed))
            .subscribe((event) => {
                const arbetsorderInfo = this.arbetsorderMap.get(event.arbetsorderId);
                if (arbetsorderInfo) {
                    this.arbetsorderDestineringsalternativWorkCompleted$.next(arbetsorderInfo);
                }
            });

    }
    private getViolStatus(hasTransmissionError: boolean, dirty: boolean) {
        if (hasTransmissionError) {
            return Viol3Status.Fel;
        }
        return dirty ? Viol3Status.Ny : Viol3Status.Aktiv;
    }

}

export class ArbetsorderEventStoreInfo {
    arbetsorderId: number;
    arbetsorderIsDirty: boolean;
    produktionsdelIsDirty: boolean;
    transportdelIsDirty: boolean;
    arbetsorderIsSending: boolean;
    hasTransmissionErrorViol3: boolean;
    senastSkickat?: string;
    alertInfo: GetArbetsorderAlertsQueryResult[];

    // Viol3
    statusProduktion: string;
    statusTransport: string;
}
